import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Note, NoteFolder } from '@td/shared';
import { CLIENT_ID, del, get, patch, post } from '../lib/api';

const NOTES = ['notes'];
const FOLDERS = ['note-folders'];

export function useNoteFolders() {
  return useQuery({ queryKey: FOLDERS, queryFn: () => get<NoteFolder[]>('/notes/folders') });
}

/** How much sits in Unfiled and in the Trash — neither of which is a folder. */
export function useNoteCounts() {
  return useQuery({
    queryKey: [...FOLDERS, 'counts'],
    queryFn: () => get<{ unfiled: number; trash: number }>('/notes/counts'),
  });
}

/** `folder` is a folder id, 'unfiled', 'trash', or '' for every live note. */
export function useNotes(folder: string, search: string) {
  return useQuery({
    queryKey: [...NOTES, folder, search],
    queryFn: () => {
      const params = new URLSearchParams();
      if (folder) params.set('folder', folder);
      if (search) params.set('q', search);
      return get<Note[]>(`/notes?${params}`);
    },
    // Typing in the search box shouldn't blank the list between keystrokes.
    placeholderData: (previous) => previous,
  });
}

export function useNote(id: string | null) {
  return useQuery({
    queryKey: [...NOTES, 'one', id],
    queryFn: () => get<Note>(`/notes/${id}`),
    enabled: !!id,
  });
}

type NoteInput = Partial<Pick<Note, 'title' | 'body' | 'folderId' | 'pinned'>>;

export function useCreateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NoteInput) => post<Note>('/notes', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTES });
      void qc.invalidateQueries({ queryKey: FOLDERS });
    },
  });
}

export function useUpdateNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: NoteInput & { id: string }) =>
      patch<Note>(`/notes/${id}`, input),
    onSuccess: (note) => {
      qc.setQueryData([...NOTES, 'one', note.id], note);
      void qc.invalidateQueries({ queryKey: NOTES });
      void qc.invalidateQueries({ queryKey: FOLDERS });
    },
  });
}

export function useDeleteNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/notes/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTES });
      void qc.invalidateQueries({ queryKey: FOLDERS });
    },
  });
}

/** Back out of the trash, either one note or the lot. */
export function useRestoreNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string | 'all'): Promise<void> => {
      if (id === 'all') await post<{ restored: number }>('/notes/trash/restore', {});
      else await post<Note>(`/notes/${id}/restore`, {});
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTES });
      void qc.invalidateQueries({ queryKey: FOLDERS });
    },
  });
}

/** The one thing in Notes that really destroys something. */
export function useEmptyTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => del('/notes/trash'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: NOTES });
      void qc.invalidateQueries({ queryKey: FOLDERS });
    },
  });
}

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; color?: string; parentId?: string | null }) =>
      post<NoteFolder>('/notes/folders', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FOLDERS }),
  });
}

export function useUpdateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...input
    }: {
      id: string;
      name?: string;
      color?: string;
      parentId?: string | null;
    }) => patch<NoteFolder>(`/notes/folders/${id}`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: FOLDERS }),
  });
}

export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/notes/folders/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: FOLDERS });
      void qc.invalidateQueries({ queryKey: NOTES });
    },
  });
}

/** Uploads a picture and hands back the URL to drop into the note body. */
export async function uploadNoteImage(file: File, noteId?: string): Promise<string> {
  const body = new FormData();
  body.append('image', file);
  if (noteId) body.append('noteId', noteId);

  // Not the shared JSON helper: a multipart body must not carry a
  // Content-Type header of our own, or the boundary is lost.
  const res = await fetch('/api/notes/images', {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-Client-Id': CLIENT_ID },
    body,
  });

  if (!res.ok) {
    const message = await res
      .json()
      .then((b: { error?: string }) => b.error)
      .catch(() => null);
    throw new Error(message ?? "That image couldn't be uploaded");
  }

  const { url } = (await res.json()) as { url: string };
  return url;
}
